import {
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ConfigContext } from '../../../context';
import { IS_PRODUCTION, TCHOP_ROOT_URL } from '../../../env';
import {
  Channel,
  channelsNewApi,
  oidApi,
  useOidConnectionsQuery,
  userApi,
} from '../../../services';
import {
  Button,
  ButtonType,
  CUSTOM_CLIENT_CONFIG,
  CookieActionType,
  FREE_LOGIN_COOKIE_KEY,
  IconLabel,
  IconLabelSizes,
  MZ_ACCOUNT_COOKIE_KEY,
  cookieOptions,
  generateCodeChallenge,
  generateRandomString,
  getOrganisationDomain,
  getWebAppUrl,
  useLoginRedirect,
} from '../../../shared';
import { Form } from '../Form';
import { FreeLogin } from './FreeLogin';
import { LoginWithEmail } from './LoginWithEmail';
import { TwoFactorAuth, TwoFactorAuthSetup, TwoFactorAuthSetupProps } from './TwoFactor';

import classNames from 'classnames';
import formClasses from '../Form/Form.module.scss';
import classes from './Login.module.scss';

export enum LoginContentType {
  SELECT = 'SELECT',
  SSO = 'SSO',
  EMAIL = 'EMAIL',
  FREE = 'FREE',
  CHANNEL = 'CHANNEL',
  SETUP_2FA = 'SETUP_2FA',
  CODE_2FA = 'CODE_2FA',
  MAGIC_LINK = 'MAGIC_LINK',
  TICKET = 'TICKET',
}

export const Login: FunctionComponent = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const location = useLocation();

  const code = new URLSearchParams(location.search).get('code');

  const loginSSO = new URLSearchParams(location.search).get('loginSSO');

  const loginSAML = new URLSearchParams(location.search).get('loginSAML');

  const loginEmail = new URLSearchParams(location.search).get('loginEmail');

  const magicLinkToken = new URLSearchParams(location.search).get('magicLinkToken');

  const ticket = new URLSearchParams(location.search).get('ticket');

  const orgDomain = useMemo(() => getOrganisationDomain(), []);

  const { data: oidConnections } = useOidConnectionsQuery(undefined, {
    skip: Boolean(magicLinkToken || ticket),
  });

  const [oidcLogin] = oidApi.endpoints.oidcLogin.useMutation();

  const [userLoginByEmail] = userApi.endpoints.userLoginByEmail.useMutation();

  const [userLoginByMagicLink] = userApi.endpoints.userLoginByMagicLink.useMutation();

  const [userLoginByAfzTicket] = userApi.endpoints.userLoginByAfzTicket.useMutation();

  const [exchangeCodeToAccessToken] = oidApi.endpoints.oidcExchangeCodeToAccessToken.useMutation();

  const [getChannels] = channelsNewApi.endpoints.channels.useLazyQuery();

  const freeLoginCookieKey = useMemo(() => `${FREE_LOGIN_COOKIE_KEY}_${orgDomain}`, [orgDomain]);

  const [cookies, setCookie] = useCookies([freeLoginCookieKey, MZ_ACCOUNT_COOKIE_KEY]);

  const { freeLoginSessionsCount = 0 } = { ...cookies[freeLoginCookieKey] };

  const { loginRedirect, clearLoginRedirect } = useLoginRedirect();

  const {
    channelLogos,
    login: {
      title,
      intro,
      logoUrl,
      SSOLabel,
      saml: { label: SAMLLabel, enabled: SAMLEnabled },
      email: { enabled: emailEnabled },
      externalLinks,
    },
    freeLogin,
    meta: { title: appTitle },
  } = useContext(ConfigContext).config.elements;

  const { isDefault: isFreeLoginDefault } = { ...freeLogin };

  const [email, setEmail] = useState<string>('');

  const [password, setPassword] = useState<string>('');

  const [loginContentType, setLoginContentType] = useState<LoginContentType | null>(null);

  const [twoFactorAuthSetup, setTwoFactorAuthSetup] = useState<TwoFactorAuthSetupProps>({
    otpAuthSecret: '',
    otpAuthUrlQRCode: '',
    onVerify: () => null,
    onBack: () => null,
  });

  const [channels, setChannels] = useState<Channel[]>([]);

  const channelsHeadingRef = useRef<HTMLHeadingElement>(null);

  const successLoginRedirect = useCallback(
    ({ webappUrl, skipLoginRedirect }: { webappUrl: string; skipLoginRedirect?: boolean }) => {
      if (loginRedirect) {
        clearLoginRedirect();

        if (!skipLoginRedirect) {
          window.location.replace(loginRedirect);
          return;
        }
      }

      IS_PRODUCTION ? window.location.replace(webappUrl) : navigate('/');
    },
    [clearLoginRedirect, loginRedirect, navigate]
  );

  const onSuccessLogin = useCallback(
    async ({ accessToken, webappUrl }: { accessToken: string; webappUrl: string }) => {
      if (CUSTOM_CLIENT_CONFIG?.baseHost) {
        setCookie(
          MZ_ACCOUNT_COOKIE_KEY,
          accessToken,
          cookieOptions({
            action: CookieActionType.SET,
            domain: `.${CUSTOM_CLIENT_CONFIG.baseHost}`,
          })
        );
      }

      const channels = await getChannels({ filters: { excludeArchived: true } }).unwrap();

      const isDeepLinkRedirect = Boolean(
        loginRedirect && new URLSearchParams(new URL(loginRedirect).search).get('r')
      );

      if (channels.length > 1 && !isDeepLinkRedirect) {
        setChannels(channels);
        setLoginContentType(LoginContentType.CHANNEL);
        return;
      }

      successLoginRedirect({ webappUrl });
    },
    [getChannels, loginRedirect, setCookie, successLoginRedirect]
  );

  const ssoProcessing = useCallback(async () => {
    const codeVerifier = window.sessionStorage.getItem('code_verifier');

    if (!code || !oidConnections?.length || !codeVerifier) {
      setLoginContentType(LoginContentType.SELECT);
      return;
    }

    const { issuer } = oidConnections[0];

    try {
      const { accessToken } = await exchangeCodeToAccessToken({
        code,
        codeVerifier,
        issuer,
        redirectUrl: window.location.href.split('?')[0],
      }).unwrap();

      const { accessToken: loginAccessToken, channel } = await oidcLogin({
        issuer,
        accessToken,
      }).unwrap();

      await onSuccessLogin({
        accessToken: loginAccessToken,
        webappUrl: getWebAppUrl(channel.subdomain),
      });
    } catch (_) {}
  }, [code, exchangeCodeToAccessToken, oidConnections, oidcLogin, onSuccessLogin]);

  useEffect(() => {
    if (CUSTOM_CLIENT_CONFIG && window.location.hostname !== CUSTOM_CLIENT_CONFIG.client) {
      const url = new URL(window.location.href);
      url.hostname = CUSTOM_CLIENT_CONFIG.client;
      window.location.replace(url.href);
      return;
    }

    if (window.location.href.includes(`-${orgDomain}`)) {
      const url = new URL(window.location.href);
      url.hostname = url.hostname.replace(/^([^.]+)-(.*)$/, '$2');
      window.location.replace(url.href);
    }
  }, [orgDomain]);

  useEffect(() => {
    if (typeof oidConnections === 'undefined') {
      return;
    }

    if (oidConnections.length) {
      if (code) {
        setLoginContentType(LoginContentType.SSO);
        ssoProcessing();
        return;
      }

      setLoginContentType(LoginContentType.SELECT);
      return;
    }

    if (isFreeLoginDefault && !freeLoginSessionsCount) {
      setLoginContentType(LoginContentType.FREE);
      return;
    }

    setLoginContentType(SAMLEnabled ? LoginContentType.SELECT : LoginContentType.EMAIL);
  }, [
    SAMLEnabled,
    code,
    freeLoginSessionsCount,
    isFreeLoginDefault,
    oidConnections,
    ssoProcessing,
  ]);

  const renderContentHeader = useMemo(
    () =>
      Boolean(
        ![LoginContentType.CHANNEL, LoginContentType.SETUP_2FA, LoginContentType.CODE_2FA].includes(
          loginContentType as LoginContentType
        ) && [title, intro, logoUrl].some((item) => Boolean(item))
      ),
    [intro, loginContentType, logoUrl, title]
  );

  const contentHeader = useMemo(() => {
    if (logoUrl) {
      return <img src={logoUrl} alt={title || appTitle} />;
    }

    return (
      <>
        {title && <h1 className={formClasses['form__content-header-title']}>{title}</h1>}
        {intro && (
          <div
            className={formClasses['form__content-header-subtitle']}
            dangerouslySetInnerHTML={{ __html: intro }}
          ></div>
        )}
      </>
    );
  }, [appTitle, intro, logoUrl, title]);

  const otpProcessing = useCallback(
    async ({ otp, otpBackup }: { otp?: string; otpBackup?: string }) => {
      const { payload } = await userLoginByEmail({ email, password, otp, otpBackup }).unwrap();

      if (payload) {
        const { accessToken, channel } = payload;

        await onSuccessLogin({ accessToken, webappUrl: getWebAppUrl(channel.subdomain) });

        return;
      }

      toast.error(t('auth.2fa-otp-invalid'));
    },
    [email, onSuccessLogin, password, t, userLoginByEmail]
  );

  const twoFactorBackHandler = useCallback(() => {
    setLoginContentType(LoginContentType.EMAIL);
  }, []);

  const loginWithSSOHandler = useCallback(async () => {
    if (!oidConnections?.length) {
      return;
    }

    setLoginContentType(LoginContentType.SSO);

    const codeVerifier = generateRandomString(70);

    const state = generateRandomString(8);

    window.sessionStorage.setItem('code_verifier', codeVerifier);

    const codeChallenge = await generateCodeChallenge(codeVerifier);

    const { clientId, authorizationEndpoint, scopes, audience } = oidConnections[0];

    const args = new URLSearchParams({
      response_type: 'code',
      client_id: clientId,
      scope: scopes,
      code_challenge_method: 'S256',
      code_challenge: codeChallenge,
      audience,
      redirect_uri: window.location.href.split('?')[0],
      state,
    });

    window.location.replace(`${authorizationEndpoint}/?${args}`);
  }, [oidConnections]);

  const loginWithSAMLHandler = useCallback(() => {
    if (!SAMLEnabled) {
      return;
    }
    window.location.replace(
      `${TCHOP_ROOT_URL}/api/webapp/sso/saml/login?organisation=${orgDomain}`
    );
  }, [SAMLEnabled, orgDomain]);

  const loginWithMagicLinkHandler = useCallback(async () => {
    if (!magicLinkToken) {
      return;
    }

    setLoginContentType(LoginContentType.MAGIC_LINK);

    const { payload, error } = await userLoginByMagicLink({ token: magicLinkToken }).unwrap();

    if (payload) {
      const { accessToken, channel } = payload;

      await onSuccessLogin({ accessToken, webappUrl: getWebAppUrl(channel.subdomain) });

      return;
    }

    switch (error.__typename) {
      case 'User2FAOtpIsRequiredError': {
        setLoginContentType(LoginContentType.CODE_2FA);
        break;
      }
      default: {
        toast.error(t('auth.magic-link-error'));
        navigate('/auth/login');
      }
    }
  }, [magicLinkToken, navigate, onSuccessLogin, t, userLoginByMagicLink]);

  const loginWithTicketHandler = useCallback(async () => {
    if (!ticket) {
      return;
    }

    setLoginContentType(LoginContentType.TICKET);

    const { payload } = await userLoginByAfzTicket({ ticket }).unwrap();

    if (payload) {
      const { accessToken, channel } = payload;

      await onSuccessLogin({ accessToken, webappUrl: getWebAppUrl(channel.subdomain) });

      return;
    }

    toast.error(t('auth.ticket-login-error'));
    navigate('/auth/login');
  }, [navigate, onSuccessLogin, t, ticket, userLoginByAfzTicket]);

  useEffect(() => {
    if (!oidConnections?.length) {
      return;
    }

    if (loginSSO) {
      loginWithSSOHandler();
    }
  }, [loginSSO, loginWithSSOHandler, oidConnections?.length]);

  useEffect(() => {
    if (loginSAML) {
      loginWithSAMLHandler();
    }
  }, [loginSAML, loginWithSAMLHandler]);

  useEffect(() => {
    if (loginEmail) {
      setLoginContentType(LoginContentType.EMAIL);
    }
  }, [loginEmail, loginSSO, loginWithSSOHandler]);

  useEffect(() => {
    if (magicLinkToken) {
      loginWithMagicLinkHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [magicLinkToken]);

  useEffect(() => {
    if (ticket) {
      loginWithTicketHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket]);

  const loginWithEmailContent = useMemo(() => {
    return (
      <>
        <LoginWithEmail
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
          onSuccessLogin={onSuccessLogin}
          setTwoFactorAuthSetup={setTwoFactorAuthSetup}
          otpProcessing={otpProcessing}
          twoFactorBackHandler={twoFactorBackHandler}
          setLoginContentType={setLoginContentType}
        />

        {Boolean(oidConnections?.length) && (
          <div
            className={classNames(
              formClasses['form__content-link'],
              formClasses['form__content-back']
            )}
            onClick={() => setLoginContentType(LoginContentType.SELECT)}
          >
            {t('login.back-to-welcome')}
          </div>
        )}
      </>
    );
  }, [
    email,
    oidConnections?.length,
    onSuccessLogin,
    otpProcessing,
    password,
    t,
    twoFactorBackHandler,
  ]);

  const loginSelectContent = useMemo(() => {
    return (
      <div className={classes['login-buttons']}>
        {Boolean(oidConnections?.length) && (
          <Button
            type={ButtonType.primary}
            label={SSOLabel ?? t('login.with-sso')}
            onClick={loginWithSSOHandler}
            fullWidth
          />
        )}
        {SAMLEnabled && (
          <Button
            type={ButtonType.primary}
            label={SAMLLabel ?? t('login.with-saml')}
            onClick={loginWithSAMLHandler}
            fullWidth
          />
        )}
        {emailEnabled && (
          <Button
            type={ButtonType.secondary}
            label={t('login.with-email')}
            onClick={() => setLoginContentType(LoginContentType.EMAIL)}
            fullWidth
          />
        )}
        <FreeLogin onSuccessLogin={onSuccessLogin} />
      </div>
    );
  }, [
    SAMLEnabled,
    SAMLLabel,
    SSOLabel,
    emailEnabled,
    loginWithSAMLHandler,
    loginWithSSOHandler,
    oidConnections?.length,
    onSuccessLogin,
    t,
  ]);

  const loader = useMemo(() => {
    return (
      <div className={classes['loader-wrapper']}>
        <Skeleton height={50} width={'100%'} />
      </div>
    );
  }, []);

  const renderChannelContent = useCallback(
    (channel: Channel) => {
      const { id, name, image } = channel;

      const logo = channelLogos.find(({ channelId }) => channelId === id)?.logoUrl;

      if (logo) {
        return <img src={logo} className={classes['channels-list-item-logo']} alt={name} />;
      }

      return (
        <span className={classes['channels-list-item-wrapper']}>
          {image && (
            <span
              className={classes['channels-list-item-icon']}
              style={{ backgroundImage: `url(${image.url})` }}
            />
          )}
          <span className={classes['channels-list-item-name']}>{name}</span>
        </span>
      );
    },
    [channelLogos]
  );

  const renderChannel = useCallback(
    (channel: Channel) => {
      const { id, subdomain } = channel;

      return (
        <button
          key={id}
          type={'button'}
          className={classes['channels-list-item']}
          onClick={() =>
            successLoginRedirect({ webappUrl: getWebAppUrl(subdomain), skipLoginRedirect: true })
          }
        >
          {renderChannelContent(channel)}
        </button>
      );
    },
    [renderChannelContent, successLoginRedirect]
  );

  const loginChannelContent = useMemo(() => {
    return (
      <>
        <div className={formClasses['form__content-header']}>
          <h1
            ref={channelsHeadingRef}
            className={classNames(
              formClasses['form__content-header-title'],
              classes['channels-title']
            )}
            tabIndex={-1}
          >
            {t('common.channels', { count: channels.length })}
          </h1>
          <div className={formClasses['form__content-header-subtitle']}>
            {t('auth.channel-select')}
          </div>
        </div>
        <div className={classes['channels']}>
          <div className={classes['channels-list']}>
            {channels.map((channel) => renderChannel(channel))}
          </div>
          <div className={classes['channels-info']}></div>
        </div>
      </>
    );
  }, [channels, renderChannel, t]);

  useEffect(() => {
    if (loginContentType !== LoginContentType.CHANNEL) {
      return;
    }

    channelsHeadingRef.current?.focus();
  }, [loginContentType]);

  const loginContent = useMemo(() => {
    switch (loginContentType) {
      case LoginContentType.SELECT:
        return loginSelectContent;
      case LoginContentType.EMAIL:
        return loginWithEmailContent;
      case LoginContentType.SSO:
      case LoginContentType.MAGIC_LINK:
      case LoginContentType.TICKET:
        return loader;
      case LoginContentType.FREE:
        return <FreeLogin onSuccessLogin={onSuccessLogin} label={t('common.get-started')} />;
      case LoginContentType.CHANNEL:
        return loginChannelContent;
      case LoginContentType.SETUP_2FA:
        return <TwoFactorAuthSetup {...twoFactorAuthSetup} />;
      case LoginContentType.CODE_2FA:
        return <TwoFactorAuth onValidate={otpProcessing} onBack={twoFactorBackHandler} />;
      default:
        return null;
    }
  }, [
    loader,
    loginChannelContent,
    loginContentType,
    loginSelectContent,
    loginWithEmailContent,
    onSuccessLogin,
    otpProcessing,
    t,
    twoFactorAuthSetup,
    twoFactorBackHandler,
  ]);

  if (['afz'].includes(getOrganisationDomain())) {
    return (
      <Form>
        <h1 className={formClasses['form__content-header-title']}>
          In Kürze neu: die dfv MeisterRunde
        </h1>
        <div className={formClasses['form__content-header-subtitle']}>
          Kurze Pause - unsere Bäcker und Metzger tüfteln zusammen am neuen Meisterstück.
        </div>
      </Form>
    );
  }

  if (!oidConnections && !magicLinkToken && !ticket) {
    return null;
  }

  return (
    <Form>
      {renderContentHeader && (
        <div className={formClasses['form__content-header']}>{contentHeader}</div>
      )}
      {loginContent}
      {Boolean(externalLinks.length) && (
        <div className={classes['external-links']}>
          {externalLinks.map(({ url, localiseKey, color, hoverColor }, index) => (
            <>
              <IconLabel
                key={localiseKey}
                label={t(localiseKey)}
                color={color}
                hoverColor={hoverColor}
                labelSize={IconLabelSizes.small}
                labelClassName={classes['external-links__label']}
                onClick={() => window.open(url, '_blank', 'noreferrer')}
              />
              {index < externalLinks.length - 1 && (
                <span className={classes['external-links__divider']}>•</span>
              )}
            </>
          ))}
        </div>
      )}
    </Form>
  );
};
